<template>
  <div class="code-section relative my-2 p-4 rounded-md" :data-language="props.lang">
    <div class="absolute top-2 right-2 flex flex-row">
      <CopyToClipboardButton :code="_code" :no-tooltip="noTooltip" />
    </div>
    <div class="overflow-auto" v-html="htmlCode" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { codeToHtml } from '@/utils/shiki';
import CopyToClipboardButton from './code/CopyToClipboardButton.vue';

const props = defineProps({
  code: { type: String, required: true },
  lang: { type: String, required: true },
  encoded: { type: Boolean, required: false },
  noTooltip: { type: Boolean, required: false },
});

const _code = computed(() => (props.encoded ? decodeURIComponent(props.code) : props.code));

const escapeHtml = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const plainCodeHtml = (code: string) => `<pre><code>${escapeHtml(code)}</code></pre>`;

const htmlCode = ref(plainCodeHtml(_code.value));

htmlCode.value = await codeToHtml(_code.value, props.lang);

watch([_code, () => props.lang], ([newCode, newLang], _prev, onCleanup) => {
  let cancelled = false;
  onCleanup(() => {
    cancelled = true;
  });
  htmlCode.value = plainCodeHtml(newCode);
  codeToHtml(newCode, newLang)
    .then(result => {
      if (!cancelled) htmlCode.value = result;
    })
    .catch(() => {});
});
</script>

<style scoped lang="scss">
.code-section {
  background-color: var(--code-snippet-bg-color, var(--vvd-color-neutral-900));
  padding: 16px;

  :deep(pre) {
    margin: 0;
  }

  :deep(code) {
    background: transparent;

    color: #999;
    text-shadow: 0 1px rgba(0, 0, 0, 0.3);

    font: var(--vvd-typography-base-extended-code);
    direction: ltr;
    text-align: left;
    white-space: pre;
    word-spacing: normal;
    word-break: normal;
    line-height: 1.5;

    tab-size: 4;
    hyphens: none;
    padding: 0;
    margin: 0;
  }
}
</style>
